import { Card, Container, Spinner } from "react-bootstrap";
import "./Login.css";
import Logo from "../common/Logo";
import logo from "../assets/logo/logo.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash, faSignIn } from "@fortawesome/free-solid-svg-icons";
import { Alert, Col, Form, Input, Row } from "antd";
import { useCallback, useState } from "react";
import classNames from "classnames";
import { getAuth, getIdToken, signInWithEmailAndPassword } from "firebase/auth";
import { useToasts } from "../contexts/toasts";
import Cookies from "js-cookie";
import { useAuth } from "../contexts/auth";
import { useNavigate } from "react-router-dom";
import { User } from "../api/User";

const LOCK_MESSAGE_KEY = "platform-lock-message";

export default function Login() {
  const [loginForm] = Form.useForm();
  const navigate = useNavigate();
  const { setToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const { showToast } = useToasts();
  // Messaggio mostrato se il Superadmin ha bloccato l'accesso al gestionale.
  const [lockMessage, setLockMessage] = useState<string | null>(() => {
    const msg = sessionStorage.getItem(LOCK_MESSAGE_KEY);
    sessionStorage.removeItem(LOCK_MESSAGE_KEY);
    return msg;
  });

  const handleLogin = useCallback(
    (values: any) => {
      const auth = getAuth();
      setLoading(true);
      signInWithEmailAndPassword(auth, values.email, values.password)
        .then((userCredential) => {
          const user = userCredential.user;
          getIdToken(user)
            .then((token) => {
              Cookies.set(
                window.__env.AUTH_TOKEN_NAME || "get-move-token",
                token
              );
              // Verifica che l'accesso non sia bloccato prima di entrare.
              User.getUserInfo(token)
                .then(
                  () => true,
                  (err) => err?.response?.status !== 423
                )
                .then((allowed) => {
                  setLoading(false);
                  if (!allowed) {
                    Cookies.remove(
                      window.__env.AUTH_TOKEN_NAME || "get-move-token"
                    );
                    // Il testo (eventualmente personalizzato) arriva dal
                    // backend tramite l'interceptor di axios.
                    setLockMessage(
                      sessionStorage.getItem(LOCK_MESSAGE_KEY) ||
                        "L'accesso al gestionale è temporaneamente sospeso."
                    );
                    sessionStorage.removeItem(LOCK_MESSAGE_KEY);
                    return;
                  }
                  setToken(token);
                  navigate("/dashboard");
                });
            })
            .catch(() => {
              setLoading(false);
              showToast(
                "user-error",
                "Errore nel recupero del token",
                "",
                "danger"
              );
            });
        })
        .catch((error) => {
          setLoading(false);
          const errorCode = error.code;

          // Mostra l'errore se le credenziali sono sbagliate
          if (errorCode === "auth/invalid-credential") {
            showToast("user-error", "Email o Password errata", "", "danger");
          } else {
            showToast("user-error", "Errore durante il login", "", "danger");
          }
        });
    },
    [navigate, setToken, showToast]
  );

  return (
    <Container className="login-container">
      <Card>
        <Card.Body>
          <Form
            className="align-items-center"
            form={loginForm}
            onFinish={handleLogin}
          >
            <div className="text-center mb-5">
              <Logo logo={logo} width={200} className="my-4" />
              <Card.Subtitle className="text-muted">
                Inserisci le tue credenziali
              </Card.Subtitle>
            </div>
            {lockMessage && (
              <Row className="justify-content-center mb-4">
                <Col xs={24} md={8}>
                  <Alert
                    type="warning"
                    showIcon
                    message="Accesso sospeso"
                    description={lockMessage}
                  />
                </Col>
              </Row>
            )}
            <Form.Item
              labelAlign="left"
              labelCol={{ xs: { span: 24 }, md: { span: 24, offset: 8 } }}
              wrapperCol={{
                xs: { span: 24 },
                md: { span: 8, offset: 8 },
              }}
              name="email"
              label="Email"
              rules={[
                {
                  required: true,
                  message: "L'indirizzo email è obbligatorio",
                },
                {
                  type: "email",
                  message: "Inserisci un indirizzo email valido",
                },
              ]}
            >
              <Input size="large" placeholder="Email..." />
            </Form.Item>
            <Form.Item
              labelAlign="left"
              labelCol={{ xs: { span: 24 }, md: { span: 24, offset: 8 } }}
              wrapperCol={{
                xs: { span: 24 },
                md: { span: 8, offset: 8 },
              }}
              name="password"
              label="Password"
              rules={[
                {
                  required: true,
                  message: "La password è obbligatoria",
                },
              ]}
            >
              <Input.Password
                placeholder="Password..."
                size="large"
                type="password"
                iconRender={(visible) => (
                  <FontAwesomeIcon icon={visible ? faEyeSlash : faEye} />
                )}
              />
            </Form.Item>
            <Row className="justify-content-center">
              <Col sm={10} md={5}>
                <button
                  type="submit"
                  disabled={loading}
                  className={classNames("w-100 button-primary", {
                    "button-disabled": loading,
                  })}
                >
                  {loading ? (
                    <Spinner size="sm" />
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faSignIn} />
                      <span className="ms-2">Login</span>
                    </>
                  )}
                </button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
}
